import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  Search,
  Filter,
  Edit,
  Trash2,
  Copy,
  Eye,
  Calendar,
  Building2,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Loader2,
  RefreshCw
} from 'lucide-react';
import proposalService, { Proposal as ApiProposal, ProposalStatus } from '../services/proposalService';

// 백엔드 status → 한글 매핑
const STATUS_LABELS: Record<string, string> = {
  draft: '작성중',
  writing: '작성중',
  reviewing: '검토중',
  submitted: '제출완료',
  selected: '선정',
  rejected: '탈락',
};

const STATUS_FILTER_MAP: Record<string, ProposalStatus[]> = {
  '작성중': ['draft', 'writing'],
  '검토중': ['reviewing'],
  '제출완료': ['submitted'],
  '선정': ['selected'],
  '탈락': ['rejected'],
};

function toKoreanStatus(status: string): string {
  return STATUS_LABELS[status] || status;
}

function formatLastUpdated(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}시간 전`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}일 전`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) return `${diffWeeks}주 전`;
  return date.toLocaleDateString('ko-KR');
}

const ProposalManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [proposals, setProposals] = useState<ApiProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: { page_size?: number; status?: string; search?: string } = {
        page_size: 50,
      };
      // 백엔드 status 필터 (영문 값 전달)
      if (selectedFilter !== 'all') {
        const mapped = STATUS_FILTER_MAP[selectedFilter];
        if (mapped && mapped.length > 0) {
          params.status = mapped[0]; // 백엔드는 단일 status 필터
        }
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const response = await proposalService.getProposals(params);
      // PaginatedResponse 또는 배열 둘 다 처리
      const items = Array.isArray(response) ? response : (response.items || []);
      setProposals(items);
    } catch (err: any) {
      console.error('Failed to fetch proposals:', err);
      setError('제안서 목록을 불러오는데 실패했습니다. 서버 연결을 확인해주세요.');
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFilter, searchQuery]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const handleEditProposal = (id: string) => {
    navigate(`/proposal-generator?edit=${id}`);
  };

  const handleDeleteProposal = async (id: string) => {
    if (!window.confirm('이 제안서를 삭제하시겠습니까?')) return;
    try {
      await proposalService.deleteProposal(id);
      setProposals(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Delete failed:', err);
      alert('제안서 삭제에 실패했습니다.');
    }
  };

  const handleCopyProposal = (proposal: ApiProposal) => {
    navigate('/proposal-generator', { state: { copyFrom: proposal } });
  };

  const handleViewProposal = (id: string) => {
    navigate(`/projects?proposal=${id}`);
  };

  // 프론트엔드 검색 (백엔드 search 미지원 시 폴백)
  const filteredProposals = proposals.filter(proposal => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (proposal.title || '').toLowerCase().includes(q) ||
      (proposal.grant_title || '').toLowerCase().includes(q) ||
      ((proposal as any).company_name || '').toLowerCase().includes(q)
    );
  });

  const getStatusColor = (status: string) => {
    const label = toKoreanStatus(status);
    switch (label) {
      case '선정': return 'bg-green-100 text-green-800';
      case '제출완료': return 'bg-blue-100 text-blue-800';
      case '검토중': return 'bg-yellow-100 text-yellow-800';
      case '작성중': return 'bg-gray-100 text-gray-800';
      case '탈락': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    const label = toKoreanStatus(status);
    switch (label) {
      case '선정': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case '제출완료': return <Send className="h-4 w-4 text-blue-500" />;
      case '검토중': return <Clock className="h-4 w-4 text-yellow-500" />;
      case '작성중': return <Edit className="h-4 w-4 text-gray-500" />;
      case '탈락': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return null;
    }
  };

  const stats = {
    total: proposals.length,
    inProgress: proposals.filter(p => ['draft', 'writing'].includes(p.status)).length,
    submitted: proposals.filter(p => ['submitted', 'reviewing'].includes(p.status)).length,
    selected: proposals.filter(p => p.status === 'selected').length,
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">제안서 관리</h1>
          <p className="text-gray-600 mt-2">작성 중인 제안서와 제출 현황을 관리합니다</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchProposals}
            className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            새로고침
          </button>
          <Link
            to="/proposal-generator"
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            새 제안서 작성
          </Link>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">전체 제안서</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">작성 중</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
            </div>
            <Edit className="h-8 w-8 text-yellow-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">제출 완료</p>
              <p className="text-2xl font-bold text-blue-600">{stats.submitted}</p>
            </div>
            <Send className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">선정</p>
              <p className="text-2xl font-bold text-green-600">{stats.selected}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-400" />
          </div>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="제안서, 공모사업, 기업명 검색..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-full"
          />
        </div>

        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="all">모든 상태</option>
            <option value="작성중">작성중</option>
            <option value="검토중">검토중</option>
            <option value="제출완료">제출완료</option>
            <option value="선정">선정</option>
            <option value="탈락">탈락</option>
          </select>
        </div>
      </div>

      {/* 에러 상태 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
          <button
            onClick={fetchProposals}
            className="px-3 py-1 text-red-600 border border-red-300 rounded hover:bg-red-100 transition-colors text-sm"
          >
            다시 시도
          </button>
        </div>
      )}

      {/* 로딩 상태 */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
          <span className="ml-3 text-gray-600">제안서 목록을 불러오는 중...</span>
        </div>
      )}

      {/* 제안서 목록 */}
      {!loading && (
        <div className="space-y-4">
          {filteredProposals.map((proposal) => (
            <div key={proposal.id} className="card hover:shadow-medium transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${getStatusColor(proposal.status)}`}>
                      {getStatusIcon(proposal.status)}
                      <span>{toKoreanStatus(proposal.status)}</span>
                    </span>
                    {proposal.evaluation_score != null && (
                      <span className="text-sm text-gray-500">
                        평가점수: <span className="font-medium text-gray-900">{proposal.evaluation_score}점</span>
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{proposal.title}</h3>
                  {proposal.grant_title && (
                    <p className="text-sm text-gray-600 mb-3">{proposal.grant_title}</p>
                  )}

                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    {(proposal as any).company_name && (
                      <div className="flex items-center space-x-1">
                        <Building2 className="h-4 w-4" />
                        <span>{(proposal as any).company_name}</span>
                      </div>
                    )}
                    {proposal.updated_at && (
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{formatLastUpdated(proposal.updated_at)}</span>
                      </div>
                    )}
                    {/* last_updated from backend list response */}
                    {!proposal.updated_at && (proposal as any).last_updated && (
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{formatLastUpdated((proposal as any).last_updated)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-4">
                  {/* 진행률 */}
                  <div className="w-32">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">진행률</span>
                      <span className="font-medium">{proposal.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${proposal.progress || 0}%` }}
                      />
                    </div>
                  </div>

                  {/* 액션 버튼 */}
                  <div className="flex items-center space-x-2">
                    <button
                      className="p-2 text-gray-400 hover:text-emerald-600 transition-colors"
                      onClick={() => handleViewProposal(proposal.id)}
                      title="보기"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      onClick={() => handleEditProposal(proposal.id)}
                      title="편집"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => handleCopyProposal(proposal)}
                      title="복사"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      onClick={() => handleDeleteProposal(proposal.id)}
                      title="삭제"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 빈 상태 */}
      {!loading && !error && filteredProposals.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {proposals.length === 0 ? '아직 작성된 제안서가 없습니다' : '제안서를 찾을 수 없습니다'}
          </h3>
          <p className="text-gray-600 mb-4">
            {proposals.length === 0 ? '새 제안서를 작성해보세요' : '검색어나 필터를 변경해보세요'}
          </p>
          <Link
            to="/proposal-generator"
            className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            첫 제안서 작성하기
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProposalManagement;
